import React, { useState, useEffect, useCallback } from "react";
import { fbSet, fbGetAll } from "../../firebase";
import imageData from "../../data/imageData";

const RARITY_ORDER = ["Common","Uncommon","Rare","Epic","Legendary","Mythic","Ultra"];
const RARITY_COLOURS = {
  Common:"#9e9e9e", Uncommon:"#4caf50", Rare:"#2196f3",
  Epic:"#9c27b0", Legendary:"#ff9800", Mythic:"#ffeb3b", Ultra:"#ff2d78",
};

const CATEGORIES = [
  { id: "rolls",      label: "🎲 Rolls",       key: "rolls",         format: n => n.toLocaleString()                },
  { id: "collected",  label: "🐹 Collection",   key: "collected",     format: n => `${n} hamsters`                   },
  { id: "coins",      label: "🪙 Coins",        key: "totalCoins",    format: n => `🪙 ${n.toLocaleString()}`        },
  { id: "trophies",   label: "🏅 Trophies",     key: "battleTrophies",format: n => `🏅 ${n}`                        },
  { id: "prestige",   label: "⭐ Prestige",      key: "prestige",      format: n => n > 0 ? `Level ${n}` : "None"    },
];


const BANNED_WORDS = [
  "fuck","shit","cunt","dick","cock","pussy","ass","bitch","bastard",
  "wank","twat","piss","slut","whore","nigger","nigga","faggot","fag","retard","retarded",
];

// Normalise leet speak before checking: 0→o, 1→i, 3→e, 4→a, 5→s, 7→t, @→a, $→s, etc.
function normaliseLeet(str) {
  return str.toLowerCase()
    .replace(/0/g, "o")
    .replace(/1/g, "i")
    .replace(/3/g, "e")
    .replace(/4/g, "a")
    .replace(/5/g, "s")
    .replace(/6/g, "g")
    .replace(/7/g, "t")
    .replace(/8/g, "b")
    .replace(/@/g, "a")
    .replace(/\$/g, "s")
    .replace(/\+/g, "t")
    .replace(/[^a-z]/g, "");
}

function containsBannedWord(str) {
  const normalised = normaliseLeet(str);
  return BANNED_WORDS.some(w => normalised.includes(w));
}

function getPlayerId() {
  let id = localStorage.getItem("playerId");
  if (!id) {
    id = "player_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem("playerId", id);
  }
  return id;
}

function getPlayerName() {
  return localStorage.getItem("playerName") || "";
}

export default function Leaderboard({ rollCount, inventory, totalCoins, prestige, battleTrophies, onClose }) {
  const [category, setCategory]     = useState("rolls");
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [playerName, setPlayerName] = useState(getPlayerName);
  const [nameInput, setNameInput]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const playerId = getPlayerId();

  const catDef = CATEGORIES.find(c => c.id === category);

  const buildPayload = useCallback((name) => ({
    name:          name.trim(),
    rolls:         rollCount,
    collected:     inventory.length,
    totalCoins,
    prestige,
    battleTrophies: battleTrophies || 0,
    rarest:        inventory.length
      ? inventory.reduce((b, i) =>
          RARITY_ORDER.indexOf(i.rarity) > RARITY_ORDER.indexOf(b) ? i.rarity : b,
          inventory[0].rarity)
      : "—",
    updatedAt: Date.now(),
  }), [rollCount, inventory, totalCoins, prestige, battleTrophies]);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await fbGetAll("lb");
      const entries = Object.entries(all || {}).map(([id, val]) => ({ id, ...val }));
      setEntries(entries);
    } catch (e) {
      console.error("Leaderboard fetch error:", e);
      setError("Couldn't load rankings — check your connection.");
      setEntries([]);
    }
    setLoading(false);
  }, []);

  const submitScore = useCallback(async (name) => {
    if (!name?.trim()) return;
    if (containsBannedWord(name)) {
      setError("That name isn't allowed — please choose another.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await fbSet(`lb/${playerId}`, buildPayload(name));
      localStorage.setItem("playerName", name.trim());
      setPlayerName(name.trim());
      setSubmitDone(true);
      await fetchLeaderboard();
    } catch (e) {
      console.error("Leaderboard save error:", e);
      setError("Couldn't save score — check your connection and try again.");
    }
    setSubmitting(false);
  }, [playerId, buildPayload, fetchLeaderboard]);

  // Auto-update score on open if already named
  useEffect(() => {
    const saved = getPlayerName();
    if (saved) fbSet(`lb/${playerId}`, buildPayload(saved));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const sorted = [...entries]
    .sort((a, b) => (b[catDef.key] || 0) - (a[catDef.key] || 0))
    .slice(0, 20);

  const yourRank = sorted.findIndex(e => e.id === playerId) + 1;

  const rankLabel = (i) => ["🥇","🥈","🥉"][i] ?? `#${i + 1}`;

  const rowClass = (i, id) => {
    let cls = "lb-row";
    if (id === playerId) cls += " you";
    else if (i === 0) cls += " top1";
    else if (i === 1) cls += " top2";
    else if (i === 2) cls += " top3";
    return cls;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box leaderboard-box" onClick={e => e.stopPropagation()}>

        <h2 style={{ marginBottom: 4, textAlign: "center" }}>🏆 Leaderboard</h2>
        <p className="leaderboard-subtitle">Global rankings</p>

        {error && <div className="lb-error">⚠️ {error}</div>}

        {/* Name prompt */}
        {!playerName ? (
          <div className="lb-name-prompt">
            <div className="lb-name-prompt-title">🐹 Enter your name to join the leaderboard</div>
            <div className="lb-name-input-row">
              <input
                className="lb-name-input"
                type="text"
                placeholder="Your name…"
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                maxLength={20}
                onKeyDown={e => e.key === "Enter" && submitScore(nameInput)}
                autoFocus
              />
              <button
                className="lb-name-submit"
                onClick={() => submitScore(nameInput)}
                disabled={submitting || !nameInput.trim()}
              >
                {submitting ? "…" : "Join"}
              </button>
            </div>
          </div>
        ) : (
          <div className="lb-name-prompt lb-name-playing">
            <div>
              <span className="lb-playing-label">Playing as</span>
              <span className="lb-playing-name">{playerName}</span>
              {submitDone && <span className="lb-score-saved">✓ Score saved</span>}
            </div>
            <button
              className="lb-change-btn"
              onClick={() => {
                localStorage.removeItem("playerName");
                setPlayerName(""); setNameInput(""); setSubmitDone(false);
              }}
            >
              Change
            </button>
          </div>
        )}

        {/* Your rank */}
        {yourRank > 0 && playerName && (
          <div className="lb-your-rank">
            <span className="lb-your-rank-label">Your rank in {catDef.label}</span>
            <span className="lb-your-rank-pos">{rankLabel(yourRank - 1)}</span>
          </div>
        )}

        {/* Category tabs */}
        <div className="lb-tabs">
          {CATEGORIES.map(c => (
            <button
              key={c.id}
              className={`lb-tab${category === c.id ? " active" : ""}`}
              onClick={() => setCategory(c.id)}
            >{c.label}</button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="lb-loading">
            <span className="lb-loading-spinner">⏳</span>
            Loading rankings…
          </div>
        ) : sorted.length === 0 ? (
          <div className="lb-empty">
            <span className="lb-empty-emoji">🐹</span>
            No entries yet — be the first!
          </div>
        ) : (
          <div className="lb-table">
            {sorted.map((entry, i) => (
              <div key={entry.id} className={rowClass(i, entry.id)}>
                <div className="lb-rank">{rankLabel(i)}</div>
                <div className="lb-player">
                  <div className="lb-player-name">
                    {entry.name || "Anonymous"}
                    {entry.id === playerId && <span className="lb-you-badge">YOU</span>}
                    {entry.prestige > 0 && <span className="lb-prestige-stars">{"⭐".repeat(Math.min(entry.prestige,5))}</span>}
                  </div>
                  <div className="lb-player-sub">
                    Rarest: <span style={{fontWeight:800, color: RARITY_COLOURS[entry.rarest] || "var(--text-muted)"}}>{entry.rarest||"—"}</span>
                    {entry.collected > 0 && ` · ${entry.collected}/${imageData.length}`}
                    {entry.battleTrophies > 0 && ` · 🏅${entry.battleTrophies}`}
                  </div>
                </div>
                <div className="lb-score">{catDef.format(entry[catDef.key] || 0)}</div>
              </div>
            ))}
          </div>
        )}

        <button className="lb-refresh-btn" onClick={fetchLeaderboard} disabled={loading}>
          {loading ? "⏳ Loading…" : "🔄 Refresh"}
        </button>
        <button className="back-button" onClick={onClose} style={{ width:"100%", marginTop:8 }}>
          Close
        </button>
      </div>
    </div>
  );
}